import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  AtsApplicationCreateFromCandidatesRecommendationsDocument,
  AtsApplicationsDataCollectionDocument,
  AtsJobPostingApplicationsPageDocument,
  JobPostingDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobMultiPostErrors } from 'modules/ats/components/JobMultiPostErrors'
import { JobPostingLayout } from 'modules/ats/components/job_postings/JobPostingLayout'
import { useJobDetailsContext } from 'modules/ats/pages/JobsPage/JobDetails'

import ApplicationsTable from './components/ApplicationsTable'

const JobApplicationsPage = () => {
  const { job } = useJobDetailsContext()
  const { access, company } = useCurrentSession()

  const { data } = useQuery(AtsJobPostingApplicationsPageDocument, {
    variables: { id: job.id, accessId: access.id, companyId: company.id },
  })

  const [addAISuggestions] = useMutation(
    AtsApplicationCreateFromCandidatesRecommendationsDocument,
    {
      extractorFn: (mutation_data) =>
        mutation_data.atsMutations.createFromCandidatesRecommendationsAtsApplication,
      refetchQueries: [
        AtsApplicationsDataCollectionDocument,
        AtsJobPostingApplicationsPageDocument,
        JobPostingDocument,
      ],
    }
  )

  const jobData = data?.ats.jobPosting
  const aiSearchEnabled = data?.features.featuresConnection.nodes.find(
    (node) => node.name === 'dev_ats_ai_search'
  )?.enabled
  if (!jobData) {
    return (
      <Box padding="s40" width="full" alignItems="center" flexGrow>
        <Spinner />
      </Box>
    )
  }

  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const isReviewerRole =
    jobData?.currentHiringRole[0]?.hiringRole === 'reviewer' && !canManageJobPostings

  const allowsNewCandidates =
    !isReviewerRole &&
    (jobData?.status === 'draft' ||
      jobData?.status === 'published' ||
      jobData?.status === 'unlisted')

  const onAddAISuggestions = async () => {
    const response = await addAISuggestions({
      variables: { jobPostingId: jobData.id },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      const applications = result.data.applications ?? []
      if (applications.length > 0) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_no_candidates'),
        })
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.job_details.add_candidate.add_ai_suggestions_error'),
      })
    }
  }

  return (
    <JobPostingLayout job={job} isReviewerRole={isReviewerRole}>
      <Box width="full" gap="s8">
        <Box gap="s16">
          <JobMultiPostErrors
            atsJobPostingId={jobData.id}
            locationId={jobData.location?.id}
            removable
          />

          {allowsNewCandidates && aiSearchEnabled && (
            <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
              <Button
                type="positive"
                iconName="ai"
                aria-label={i18n.t('ats.job_details.add_candidate.add_ai_suggestions')}
                onClick={() => onAddAISuggestions()}
              >
                {i18n.t('ats.job_details.add_candidate.add_ai_suggestions')}
              </Button>
            </Box>
          )}
        </Box>
        {/* TO DO: we should pass the job data to the ApplicationsTable */}
        <ApplicationsTable jobId={jobData.id} allowsNewCandidates={allowsNewCandidates} />
      </Box>
    </JobPostingLayout>
  )
}

export default JobApplicationsPage
